import React, { useState } from 'react'
import { View, TouchableOpacity, ViewStyle } from 'react-native'
import { color } from '../../theme/color'

import { Text } from '../text'
import { Medal } from '../medal'
import { SvgIcon } from '../svg-icon'
import { iconQuizLock } from '../../assets/icons/generated/icon-QuizLock.icon'
import { DateTime } from 'luxon'
import { useInterval } from '@trivie/core'
import { iconQuizPaused, iconQuizPausedAlt } from '../../assets/icons'
import { RecallWheel } from '../recall-wheel'

const areEqual = (prevProps: any, nextProps: any) => {
  const { quiz } = nextProps
  const { quiz: prevQuiz } = prevProps

  /*if the props are equal, it won't update*/
  let _areEqual = quiz.quiz_id === prevQuiz.quiz_id
  if (_areEqual && quiz.result_id) _areEqual = quiz.result_id === prevQuiz.result_id
  if (_areEqual && quiz.completed_attempts)
    _areEqual = quiz.completed_attempts === prevQuiz.completed_attempts
  if (_areEqual && quiz.knowledge_pct) _areEqual = quiz.knowledge_pct === prevQuiz.knowledge_pct

  return _areEqual
}

export const QuizMedalRow = React.memo((props: any) => {
  const { quiz, topic, onPress, style, maxAttempts, locked, lockedByPretest, ...rest } = props

  const [unlockTime, setUnlockTime] = useState<any>(DateTime.fromISO(quiz.unlocks_at).toRelative())
  const [forceUnlock, setForceUnlock] = useState<any>(false)

  useInterval(() => {
    if (locked && !forceUnlock) {
      const t = DateTime.fromISO(quiz.unlocks_at).diffNow('seconds').seconds
      if (t / 60 <= 0) {
        setForceUnlock(true)
      }
      if (t / 60 < 1) {
        setUnlockTime(DateTime.fromISO(quiz.unlocks_at).toRelative())
      }
    }
  }, 1000)

  const getMedalKey = (medalLevel: string | null) => {
    if (quiz?.in_progress) {
      return 'Quiz.inProgress'
    } else if (quiz?.completed_attempts === 0) {
      return 'Topic.notStarted'
    } else if (!medalLevel) {
      return 'Quiz.noMedal'
    } else if (medalLevel === 'Gold') {
      return 'Quiz.goldMedal'
    } else if (medalLevel === 'Silver') {
      return 'Quiz.silverMedal'
    } else if (medalLevel === 'Bronze') {
      return 'Quiz.bronzeMedal'
    } else {
      throw new Error(`Not sure what medal ${medalLevel} is`)
    }
  }

  return (
    <TouchableOpacity
      disabled={
        lockedByPretest ||
        Boolean(!topic ? quiz.unlocks_after : quiz.unlocks_after && topic && topic.keep_item_order)
      }
      // @ts-ignore
      dataSet={{ qat: `quiz-id-${quiz.quiz_id}` }}
      onPress={onPress}
    >
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          borderRadius: 20,
          backgroundColor: color.white,
          opacity: 1,
          ...style,
        }}
      >
        <View
          style={{ ...CONTENT, opacity: !forceUnlock && (locked || lockedByPretest) ? 0.5 : 1 }}
        >
          <View
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              paddingVertical: 24.5,
            }}
          >
            <Text
              numberOfLines={1}
              variant="titleEmphasized"
              text={quiz?.title}
              style={{
                marginBottom: 2,
                paddingRight: 8,
                color: color.shade70,
              }}
            />
            {!topic && quiz?.unlocks_after ? (
              <Text
                variant="body2Medium"
                tx="Quiz.unlocksAfter"
                txOptions={{ quiz: quiz.unlocks_after }}
                style={{ color: color.shade50 }}
              />
            ) : quiz.unlocks_after && topic && topic.keep_item_order ? (
              <Text
                variant="body2Medium"
                tx="Quiz.unlocksAfter"
                txOptions={{ quiz: quiz.unlocks_after }}
                style={{ color: color.shade50 }}
              />
            ) : (locked || lockedByPretest) && !forceUnlock ? (
              <Text
                variant="body2Medium"
                tx={
                  lockedByPretest ? 'Quiz.lockedPretest' : unlockTime ? 'Quiz.lockedSubtitle' : ''
                }
                txOptions={{
                  minutes: unlockTime,
                  attempts: quiz?.completed_attempts,
                }}
                style={{ color: color.shade50 }}
              />
            ) : quiz?.completed_attempts >= maxAttempts ? null : (
              <View style={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'row' }}>
                <Text
                  variant="body2Medium"
                  tx={getMedalKey(quiz.medal_level)}
                  style={{ color: color.shade50 }}
                />
              </View>
            )}
          </View>

          {(lockedByPretest && !forceUnlock) ||
          (!topic ? quiz.unlocks_after : quiz.unlocks_after && topic && topic.keep_item_order) ? (
            <View style={{ marginLeft: 20 }}>
              <SvgIcon size={42} src={iconQuizLock} />
            </View>
          ) : locked && !forceUnlock ? (
            <View style={{ justifyContent: 'center', flexDirection: 'row' }}>
              {quiz.medal_level ? (
                <View>
                  <Medal
                    recall={quiz.knowledge_pct}
                    level={
                      quiz.completed_attempts === 0 && !quiz.in_progress
                        ? 'NotStarted'
                        : quiz.medal_level
                    }
                    size={42}
                  />
                  <View style={{ position: 'absolute', top: 14, left: 14 }}>
                    <SvgIcon src={iconQuizPaused} />
                  </View>
                </View>
              ) : quiz?.completed_attempts > 0 ? (
                <View>
                  <RecallWheel
                    hideIcon
                    variant="medium-large"
                    value={quiz?.knowledge_pct}
                    circleColour="rgb(129, 52, 254)"
                    iconColour="rgb(129, 52, 254)"
                    strokeColor="rgb(216, 202, 254)"
                  />
                  <View style={{ position: 'absolute', top: 14, left: 14 }}>
                    <SvgIcon src={iconQuizPaused} />
                  </View>
                </View>
              ) : (
                <SvgIcon size={42} src={iconQuizPausedAlt} />
              )}
            </View>
          ) : quiz.medal_level ? (
            <Medal
              recall={quiz.knowledge_pct}
              level={
                quiz.medal_level
                  ? quiz.medal_level
                  : quiz.completed_attempts === 0
                    ? 'NotStarted'
                    : quiz.medal_level
              }
              size={42}
            />
          ) : quiz.completed_attempts > 0 ? (
            <RecallWheel
              variant="medium-large"
              icon="check"
              value={quiz?.knowledge_pct}
              circleColour="rgb(129, 52, 254)"
              iconColour="rgb(129, 52, 254)"
              strokeColor="rgb(216, 202, 254)"
            />
          ) : (
            <Medal
              recall={quiz.knowledge_pct}
              level={
                quiz.medal_level
                  ? quiz.medal_level
                  : quiz.completed_attempts === 0
                    ? 'NotStarted'
                    : quiz.medal_level
              }
              size={42}
            />
          )}
        </View>
      </View>
    </TouchableOpacity>
  )
}, areEqual)

const CONTENT: ViewStyle = {
  display: 'flex',
  backgroundColor: color.white,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: 20,
  height: 84,
  borderWidth: 1,
  borderColor: '#EFF0F6',
  paddingLeft: 20,
  paddingRight: 20,
}
